import { useState, useEffect, useMemo } from 'react';
import { Location, EclipseEvent, EclipsePathData, upcomingEclipses, generateEclipsePathData } from '@/lib/eclipse-utils';

export interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  total: number;
}

export const useEclipseData = (selectedLocation: Location | null, selectedEclipseIndex: number = 0) => {
  const [nextEclipse, setNextEclipse] = useState<EclipseEvent | null>(null);
  const [eclipseInfo, setEclipseInfo] = useState<any>(null);
  const [eclipsePaths, setEclipsePaths] = useState<EclipsePathData[]>([]);
  const [isLoadingPaths, setIsLoadingPaths] = useState(false);
  const [isLoadingEclipseInfo, setIsLoadingEclipseInfo] = useState(false);
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    total: 0
  });

  // Memoize available eclipses to avoid recalculation
  const availableEclipses = useMemo(() => {
    return upcomingEclipses.filter(eclipse => eclipse.date > new Date());
  }, []);

  // Set initial eclipse immediately without heavy calculations
  useEffect(() => {
    if (availableEclipses.length > selectedEclipseIndex) {
      const eclipse = availableEclipses[selectedEclipseIndex];
      setNextEclipse(eclipse);
    }
  }, [availableEclipses, selectedEclipseIndex]);

  // Generate eclipse paths asynchronously with timeout
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let isCancelled = false;

    const generatePaths = async () => {
      setIsLoadingPaths(true);
      
      // Add a timeout to prevent hanging
      timeoutId = setTimeout(() => {
        if (!isCancelled) {
          console.warn('Eclipse path generation timeout - skipping for performance');
          setIsLoadingPaths(false);
        }
      }, 5000);

      try {
        // Use setTimeout to make this async and non-blocking
        setTimeout(() => {
          if (!isCancelled) {
            try {
              const paths = generateEclipsePathData();
              setEclipsePaths(paths);
            } catch (error) {
              console.error('Failed to generate eclipse paths:', error);
              setEclipsePaths([]); // Fallback to empty paths
            } finally {
              setIsLoadingPaths(false);
              clearTimeout(timeoutId);
            }
          }
        }, 100);
      } catch (error) {
        console.error('Failed to generate eclipse paths:', error);
        setEclipsePaths([]);
        setIsLoadingPaths(false);
      }
    };
    
    generatePaths();

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, []);

  // Calculate eclipse visibility with optimized caching
  useEffect(() => {
    if (!selectedLocation || !nextEclipse) {
      setEclipseInfo(null);
      return;
    }

    let timeoutId: NodeJS.Timeout;
    let isCancelled = false;

    const calculateVisibility = () => {
      setIsLoadingEclipseInfo(true);

      // Reduced debounce for better responsiveness
      timeoutId = setTimeout(() => {
        if (!isCancelled) {
          try {
            // The calculation is now cached, so it should be fast on subsequent calls
            const info = nextEclipse.visibleFromLocation(selectedLocation);
            console.log('Eclipse calculation result:', info); // Debug log
            if (!isCancelled) {
              setEclipseInfo(info);
              setIsLoadingEclipseInfo(false);
            }
          } catch (error) {
            console.error('Failed to calculate eclipse visibility:', error);
            if (!isCancelled) {
              // Provide fallback data
              setEclipseInfo({
                visible: false,
                localTime: nextEclipse.date,
                coverage: 0,
                magnitude: 0
              });
              setIsLoadingEclipseInfo(false);
            }
          }
        }
      }, 100); // Reduced to 100ms debounce for better responsiveness
    };

    calculateVisibility();

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [selectedLocation, nextEclipse]);

  // Update countdown timer - use location-specific time when available
  useEffect(() => {
    if (!nextEclipse) return;

    const updateCountdown = () => {
      const now = new Date().getTime();
      
      // Use location-specific eclipse time if available, otherwise use global time
      const targetTime = eclipseInfo?.localTime || eclipseInfo?.peak || nextEclipse.date;
      const eclipseTime = targetTime.getTime();
      const difference = eclipseTime - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000),
          total: difference
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 });
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [nextEclipse, eclipseInfo]);

  return {
    nextEclipse,
    eclipseInfo,
    eclipsePaths,
    isLoadingPaths,
    timeLeft,
    availableEclipses
  };
};
